// 先載入 Bootstrap JS
import 'bootstrap'
// 載入 Lottie
import lottie from 'lottie-web'
// 只引入 Font Awesome CSS
import '@fortawesome/fontawesome-free/css/all.css';
// 引入 GSAP
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import {PageFlip} from 'page-flip';

// 載入 SVG Injector
import SVGInjector from 'svg-injector';

// import Swiper bundle with all modules installed
import Swiper from 'swiper/bundle';
// import styles bundle
import 'swiper/css/bundle';

// 引入 lightGallery
import lightGallery from 'lightgallery';
// 引入 lightGallery 插件
import lgZoom from 'lightgallery/plugins/zoom';
// 引入 lightGallery CSS
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';

// 載入 SCSS（Webpack 會自動把 CSS 注入 head）
import '../scss/main.scss'


// 引入Json檔案
import kv_xl from '../lottie/kv_graph_xl.json'
import kv_xs from '../lottie/kv_graph_xs.json'
import kvTitleXL from '../lottie/kv_title_xl.json'
import kvTitleXS from '../lottie/kv_title_xs.json'


// 主視覺背景 Lottie
document.addEventListener('DOMContentLoaded', () => {
  const lottieTitle = document.querySelector('#lottieTitle')
  const kvContainer = document.getElementById('kvBgLottie');
  if (!kvContainer) {
    // console.warn('kvBgLottie container not found');
    return;
  }

  // 初始完全隱藏 kvContainer，並設定 transition 以實現漸變效果
  kvContainer.style.opacity = '0';
  kvContainer.style.visibility = 'hidden';
  kvContainer.style.transition = 'opacity 1s ease-in-out, visibility 0s 0s';

  if (lottieTitle) {
    let titleAnimation = null;
    let currentTitleSource = null;

    const chooseTitleSource = () => (window.innerWidth >= 992 ? kvTitleXL : kvTitleXS);

    const loadTitleLottie = (source) => {
      if (titleAnimation) {
        try { titleAnimation.destroy(); } catch (e) { /* ignore */ }
        titleAnimation = null;
      }

      const opts = {
        container: lottieTitle,
        renderer: 'svg',
        loop: false,
        autoplay: true
      };

      if (typeof source === 'string') {
        opts.path = source;
      } else if (typeof source === 'object' && source !== null) {
        opts.animationData = source;
      } else {
        return;
      }

      try {
        titleAnimation = lottie.loadAnimation(opts);
        currentTitleSource = source;
        
        // 監聽 title 動畫完成事件
        titleAnimation.addEventListener('complete', () => {
          // title 動畫完成後，漸變顯示 kvContainer
          kvContainer.style.visibility = 'visible';
          kvContainer.style.transition = 'opacity 1s ease-in-out';
          // 使用 setTimeout 確保 visibility 改變後再改變 opacity
          setTimeout(() => {
            kvContainer.style.opacity = '1';
          }, 10);
        });
      } catch (err) {
        // console.warn('title lottie load error', err);
      }
    };

    // initial
    loadTitleLottie(chooseTitleSource());

    // responsive switch（獨立 debounce，避免與 kv 的 resize timer 衝突）
    let titleResizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(titleResizeTimer);
      titleResizeTimer = setTimeout(() => {
        const s = chooseTitleSource();
        if (s !== currentTitleSource) loadTitleLottie(s);
      }, 150);
    });
  }
  // 確保父容器有定位，避免 absolute 失效
  const kvParent = kvContainer.closest('#kv') || kvContainer.parentElement;
  if (kvParent && window.getComputedStyle(kvParent).position === 'static') {
    kvParent.style.position = 'relative';
  }

  // 基本樣式保證可見
  Object.assign(kvContainer.style, {
    width: '100%',
    height: kvContainer.style.height || '100%',
    zIndex: '0',
    pointerEvents: 'none',
  });

  // 調整當父容器高度為 0 時的 fallback
  const ensureSize = () => {
    const parentRect = kvParent ? kvParent.getBoundingClientRect() : kvContainer.getBoundingClientRect();
    if (parentRect.height <= 0) {
      kvContainer.style.height = window.innerWidth >= 992 ? '100vh' : '100%';
      // console.info('kvBgLottie: parent had 0 height — applied fallback height');
    } else {
      kvContainer.style.height = '100%';
    }
  };
  ensureSize();

  let bgAnimation = null;
  let currentSource = null;

  const chooseSource = () => (window.innerWidth >= 992 ? kv_xl : kv_xs);

  const loadBgLottie = (source) => {
    if (bgAnimation) {
      try { bgAnimation.destroy(); } catch (e) { console.warn('destroy bgAnimation err', e); }
      bgAnimation = null;
    }

    const opts = {
      container: kvContainer,
      renderer: 'svg',
       loop: false,
       autoplay: false
    };

    if (typeof source === 'string') {
      opts.path = source;
    } else if (typeof source === 'object' && source !== null) {
      opts.animationData = source;
    } else {
      // console.warn('lottie: unsupported source', source);
      return;
    }

    try {
      bgAnimation = lottie.loadAnimation(opts);
      currentSource = source;

      // --- 新增：設定循環段落（frame range） ---
      // 預設起始/結束幀（可改為你需要的數字）
      // 也可以在外部設定 window.KV_LOOP_START / window.KV_LOOP_END 以便動態調整
      setTimeout(() => {
        try {
          const totalFrames = Math.round(bgAnimation.getDuration(true)); // 取得總幀數
          const introStart = 0;
          const introEnd = (typeof window.KV_INTRO_END === 'number') ? window.KV_INTRO_END : 180; // 預設 0 -> 180
          const loopStart = (typeof window.KV_LOOP_START === 'number') ? window.KV_LOOP_START : introEnd; // 預設從 introEnd 開始
          const loopEnd = (typeof window.KV_LOOP_END === 'number') ? window.KV_LOOP_END : totalFrames; // 預設到最後一幀

          // 驗證範圍
          const validIntro = introStart >= 0 && introEnd > introStart && introEnd <= totalFrames;
          const validLoop = loopStart >= 0 && loopEnd > loopStart && loopEnd <= totalFrames;

          // 先播放 intro（一次），完成後切換為 loop 段落
          if (validIntro) {
            try { bgAnimation.stop(); } catch (e) {}
            bgAnimation.loop = false;
            // 播放 intro 段落一次
            bgAnimation.playSegments([introStart, introEnd], true);

            const onComplete = () => {
              // 移除監聽，避免之後每次 loop 完成都觸發
              try { bgAnimation.removeEventListener('complete', onComplete); } catch (e) {}
              if (validLoop) {
                bgAnimation.loop = true;
                bgAnimation.playSegments([loopStart, loopEnd], true);
                // console.info('kvBgLottie: switched to loop segment', loopStart, loopEnd, 'totalFrames:', totalFrames);
              } else {
                // 若 loop 範圍不合法就播放整段循環（保守 fallback）
                bgAnimation.loop = true;
                bgAnimation.play();
                // console.info('kvBgLottie: invalid loop range, playing full animation. totalFrames:', totalFrames);
              }
            };

            // 在完成 intro 時觸發（只綁一次）
            bgAnimation.addEventListener('complete', onComplete);
            // console.info('kvBgLottie: playing intro segment', introStart, introEnd, 'totalFrames:', totalFrames);
          } else if (validLoop) {
            // 若 intro 不合法，直接進入 loop 段落
            bgAnimation.loop = true;
            bgAnimation.playSegments([loopStart, loopEnd], true);
            // console.info('kvBgLottie: intro invalid, directly playing loop', loopStart, loopEnd, 'totalFrames:', totalFrames);
          } else {
            // 全部 fallback
            bgAnimation.loop = true;
            bgAnimation.play();
            // console.info('kvBgLottie: both intro/loop invalid, playing full animation. totalFrames:', totalFrames);
          }

          // 小檢查：確認 svg 是否建立
          const hasSvg = !!kvContainer.querySelector('svg');
          // console.info('kvBgLottie loaded — hasSVG:', hasSvg, 'currentSource:', currentSource);
          if (!hasSvg) {
            kvContainer.style.background = 'rgba(255,0,0,0.06)'; // 臨時可視化
          }
        } catch (e) {
          // console.warn('kvBgLottie loop setup failed', e);
        }
      }, 50);
      // --- end 新增 ---
    } catch (err) {
      // console.error('lottie.loadAnimation error', err);
    }
  };

  // 初始載入 kvContainer 動畫（但保持隱藏狀態）
  loadBgLottie(chooseSource());

  // responsive switch + size check (debounced)
  let resizeTimer = null;
  
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ensureSize();
      const s = chooseSource();
      if (s !== currentSource) loadBgLottie(s);
    }, 150);
  });
});

// 註冊 ScrollToPlugin 插件
gsap.registerPlugin(ScrollToPlugin);

// 回到頂端按鈕
const goTopBtn = document.getElementById('goTopTop');
if (goTopBtn) {
  // click to scroll to top
  goTopBtn.addEventListener('click', () => {
    gsap.to(window, { scrollTo: { y: 0 }, duration: 0 });
  });

  // 更新回到頂端按鈕的顯示狀態
  const updateGoTopVisibility = () => {
    const atTop = window.scrollY === 0;
    if (atTop) {
      goTopBtn.classList.remove('is-visible');
      goTopBtn.setAttribute('aria-hidden', 'true');
    } else {
      goTopBtn.classList.add('is-visible');
      goTopBtn.setAttribute('aria-hidden', 'false');
    }
  };

  // 預設隱藏按鈕
  goTopBtn.setAttribute('aria-hidden', 'true');
  updateGoTopVisibility();

  //  優化 scroll 事件的處理
  let goTopResizeTimer = null;
  window.addEventListener('scroll', () => {
    if (goTopResizeTimer) clearTimeout(goTopResizeTimer);
    goTopResizeTimer = setTimeout(updateGoTopVisibility, 50);
  }, { passive: true });
}

// 註冊 ScrollTrigger 插件
gsap.registerPlugin(ScrollTrigger);



//主視覺 蝴蝶 ScrollTrigger
document.addEventListener('DOMContentLoaded', () => {
  
  const kvButterfly = document.querySelector('.kv_butterfly');
  if (kvButterfly) {
    gsap.to(kvButterfly, {
      yPercent: -20, // 向上移動 20%
      ease: 'none',
      scrollTrigger: {
        trigger: kvButterfly,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.6, 
        invalidateOnRefresh: true,
      }
    });
  }
  // let stInstance = null;

  // const createParallax = () => {
  //   // clean up previous instance
  //   if (stInstance) {
  //     try { stInstance.kill(); } catch (e) {}
  //     stInstance = null;
  //   }
  //   gsap.set(butterfly, { y: 0 });
  //   const yPercent = '-25';

  //   stInstance = gsap.to(butterfly, {
  //     yPercent,
  //     ease: 'none',
  //     scrollTrigger: {
  //       trigger: '#kv',
  //       start: 'top top',
  //       end: 'bottom top',
  //       scrub: 0.6,
  //       invalidateOnRefresh: true,
  //     }
  //   });
  // };

  // let resizeTimer = null;
  // createParallax();
  // window.addEventListener('resize', () => {
  //   clearTimeout(resizeTimer);
  //   resizeTimer = setTimeout(() => {
  //     createParallax();
  //     ScrollTrigger.refresh();
  //   }, 50);
  // });



});


// 另外對 #butterflyImg 與 #butterflyImg2 添加視差效果（與上方 kv_butterfly 類似）
  const bf1 = document.querySelector('#butterflyImg');
  const bf2 = document.querySelector('#butterflyImg2');
if (bf1) {
  gsap.to(bf1, {
    yPercent: -70, // 向上移動 70%
    ease: 'none',
    scrollTrigger: {
      trigger: bf1,
      start: 'bottom bottom',
      end: 'top top',
      scrub: 1, 
      invalidateOnRefresh: true,
    }
  });
}
if (bf2) {
  gsap.to(bf2, {
    yPercent: -70, // 向上移動 70%
    ease: 'none',
    scrollTrigger: {
      trigger: bf2,
      start: 'bottom bottom',
      end: 'top top',
      scrub: 1,
      invalidateOnRefresh: true,
    }
  });
}
// document.addEventListener('DOMContentLoaded', () => {
//   const bf1 = document.querySelector('#butterflyImg');
//   const bf2 = document.querySelector('#butterflyImg2');
//   if (!bf1 && !bf2) return;

//   let instances = [];
//   const setupButterflyParallax = () => {
//     // 清除舊的 instances
//     instances.forEach(i => { try { i.kill(); } catch(e){} });
//     instances = [];

//     const createFor = (el, direction = 1) => {
//       if (!el) return;
//       // reset
//       gsap.set(el, { y: 0, x: 0 });

//       // 根據視窗寬度調整強度
//       const isWide = window.innerWidth >= 992;
//       // yPercent 讓元素垂直移動，x 則做微量橫移，direction 用於讓兩隻蝴蝶移動方向不同
//       const yPercent = isWide ? -90 * direction : -120 * direction;
//       // 不做水平位移，只做垂直 yPercent 的視差
//       const anim = gsap.to(el, {
//         yPercent,
//         ease: 'none',
//         scrollTrigger: {
//           trigger: el.closest('section') || el, // 優先以最近的 section 為 trigger
//           start: 'top bottom',
//           end: 'bottom top',
//           scrub: 0.6,
//           invalidateOnRefresh: true,
//         }
//       });

//       instances.push(anim);
//     };

//   createFor(bf1, 1);
//   // 改為向左飛，與 bf1 相同方向參數（但可以用不同強度調整）
//   createFor(bf2, 1);
//   };

//   let resizeTimer2 = null;
//   setupButterflyParallax();
//   window.addEventListener('resize', () => {
//     clearTimeout(resizeTimer2);
//     resizeTimer2 = setTimeout(() => {
//       setupButterflyParallax();
//       ScrollTrigger.refresh();
//     }, 50);
//   });
// });

//gsap sc2-4-1butterfly視差滾動


const b3 = document.querySelector('#sc2-4-1butterfly');
if (b3) {
  gsap.to(b3, {
    yPercent: -50,
    ease: 'none',
    scrollTrigger: {
      trigger: b3,
      start: 'bottom bottom',
      end: 'top top',
      scrub: 1, 
      invalidateOnRefresh: true,
    }
  });
}
const b4 = document.querySelector('#sc2-4-2butterfly');
if (b4) {
  gsap.to(b4, {
    yPercent: -50,
    ease: 'none',
    scrollTrigger: {
      trigger: b4,
      start: 'bottom bottom',
      end: 'top top',
      scrub: 1, 
      invalidateOnRefresh: true,
    }
  });
}

// 固定 main_nav 且避免下方 #sc1 跳動的處理
document.addEventListener('DOMContentLoaded', () => {
  const nav = document.getElementById('main_nav');
  if (!nav) return;

  // 建立 placeholder（佔位）
  let placeholder = document.createElement('div');
  placeholder.style.width = '100%';
  placeholder.style.height = '0px';
  placeholder.style.pointerEvents = 'none';
  placeholder.style.background = '#99CFD2';
  nav.parentNode.insertBefore(placeholder, nav);

  let navInitTop = nav.getBoundingClientRect().top + window.scrollY;
  let st = null;

  // 工具函式（頂部固定 / 底部固定 / 取消固定）
  const setNavFixedTop = () => {
    const h = nav.offsetHeight;
    // 手機版不要把 placeholder 拉高
    if (window.innerWidth >= 992) {
      placeholder.style.height = h + 'px';
    } else {
      placeholder.style.height = '0px';
    }
    nav.classList.add('is-fixed');
    Object.assign(nav.style, {
      position: 'fixed',
      top: '0',
      bottom: '',
      left: '0',
      right: '0',
      zIndex: '1000'
    });
  };

  const setNavFixedBottom = () => {
    const h = nav.offsetHeight;
    // 手機版不要把 placeholder 拉高（bottom 固定為 overlay）
    if (window.innerWidth >= 992) {
      placeholder.style.height = h + 'px';
    } else {
      placeholder.style.height = '0px';
    }
    nav.classList.add('is-fixed');
    Object.assign(nav.style, {
      position: 'fixed',
      bottom: '0',
      top: '',
      left: '0',
      right: '0',
      zIndex: '1000'
    });
  };

  const unsetNavFixed = () => {
    nav.classList.remove('is-fixed');
    Object.assign(nav.style, {
      position: '',
      top: '',
      bottom: '',
      left: '',
      right: '',
      zIndex: ''
    });
    placeholder.style.height = '0px';
  };

  const syncNavStateFromScroll = () => {
    const isNarrow = window.innerWidth < 992;
    if (isNarrow) {
      // 在窄螢幕時保持底部固定（不由捲動控制）
      if (!nav.classList.contains('is-fixed')) setNavFixedBottom();
      return;
    }

    // 桌機：滾動觸發頂部固定
    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY >= navInitTop) {
      if (!nav.classList.contains('is-fixed')) setNavFixedTop();
    } else {
      if (nav.classList.contains('is-fixed')) unsetNavFixed();
    }
  };

  const createScrollTrigger = () => {
    // 只在桌機建立 ScrollTrigger
    if (st) return;
    st = ScrollTrigger.create({
      trigger: nav,
      start: 'top+=1 top', // 避免頂邊誤判
      end: '+=99999',
      onEnter: () => setNavFixedTop(),
      onLeaveBack: () => unsetNavFixed(),
      invalidateOnRefresh: true
    });
  };

  const killScrollTrigger = () => {
    if (st) {
      try { st.kill(); } catch (e) {}
      st = null;
    }
  };

  // 初始化
  const init = () => {
    const isNarrow = window.innerWidth < 992;

    // 預設先取消固定，之後根據寬度決定行為
    unsetNavFixed();

    placeholder.style.background = isNarrow ? '#017F54' : '#99CFD2';

    // 重新計算初始 top（桌機會使用）
    navInitTop = nav.getBoundingClientRect().top + window.scrollY;

    if (isNarrow) {
      // 窄螢幕：一直固定在底部，不建立 ScrollTrigger，且 placeholder 保持 0 高度
      killScrollTrigger();
      setNavFixedBottom();
    } else {
      // 桌機：使用 ScrollTrigger 決定是否固定（頂部）
      unsetNavFixed();
      createScrollTrigger();
      ScrollTrigger.refresh();
      syncNavStateFromScroll();
    }
  };

  // 頁面初始狀態
  init();

  // 資源載入完成後再 refresh 一次（保險）
  window.addEventListener('load', () => {
    init();
  });

  // 監聽 resize（跨 breakpoint 時重新計算）
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      init();
    }, 150);
  });

  // 初次 scroll 同步（被動監聽）
  window.addEventListener('scroll', () => {
    // 只有桌機需要根據 scroll 同步狀態
    if (window.innerWidth >= 992) {
      syncNavStateFromScroll();
    }
  }, { passive: true });

  // 點擊 main_nav 的錨點時，滾動到目標位置並根據螢幕寬度預留空間
  const navLinks = nav.querySelectorAll('a[href^="#"]');
  navLinks.forEach(link => {
    link.addEventListener('click', function(e) {
      const href = this.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const offsetTop = targetElement.getBoundingClientRect().top + window.scrollY;
          
          // 根據不同的錨點設定不同的偏移量
          let offset;
          
          if (window.innerWidth >= 1200) {
            // 大桌面版（>= 1200px）：特定錨點預留 170px，其他預留 63px
            switch(targetId) {
              case 'sc3-1':
                offset = 170;
                break;
              case 'sc4-1':
                offset = 170;
                break;
              case 'sc5-1':
                offset = 170;
                break;
              default:
                offset = 98;
            }
          } else if (window.innerWidth >= 992) {
            // 桌面版（992px ~ 1199px）：特定錨點預留 170px，其他預留 98px
            switch(targetId) {
              case 'sc3-1':
                offset = 120;
                break;
              case 'sc4-1':
                offset = 120;
                break;
              case 'sc5-1':
                offset = 140;
                break;
              default:
                offset = 63;
            }
          } else {
            // 手機版（< 992px）：針對特定錨點設定不同的偏移量
            switch(targetId) {
              case 'sc3-1':
                offset = 50;
                break;
              case 'sc4-1':
                offset = 50;
                break;
              case 'sc5-1':
                offset = 50;
                break;
              default:
                offset = 0;
            }
          }
          
          const scrollToPosition = offsetTop - offset;
          
          window.scrollTo({
            top: scrollToPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  });
});

// 手機版第二導航固定功能
function initializeMobileSecondNav(navId) {
  const mobileNav = document.getElementById(navId);
  if (!mobileNav) return;

  // 找到對應的區塊（例如 sc3MobileSecondNav 對應 #sc3）
  const sectionId = navId.replace('MobileSecondNav', '');
  const section = document.getElementById(sectionId);
  if (!section) return;
    // 取得 sc2-1 區塊（僅 sc2MobileSecondNav 需處理）
    // 只在 sc2-2, sc2-3, sc2-4 顯示 sc2MobileSecondNav
    let sc2_2 = null, sc2_3 = null, sc2_4 = null;
    if (navId === 'sc2MobileSecondNav') {
      sc2_2 = document.getElementById('sc2-2');
      sc2_3 = document.getElementById('sc2-3');
      sc2_4 = document.getElementById('sc2-4');
    }

    // sc5MobileSecondNav: 不在 sc5-1 顯示
    let sc5_1 = null;
    if (navId === 'sc5MobileSecondNav') {
      sc5_1 = document.getElementById('sc5-1');
    }

  // 建立 placeholder（插入在 nav 前，保留原本高度）
  let placeholder = document.createElement('div');
  placeholder.style.width = '100%';
  placeholder.style.height = '0px';
  placeholder.style.pointerEvents = 'none';
  mobileNav.parentNode.insertBefore(placeholder, mobileNav);

  // 添加過渡效果，讓顯示/隱藏更平滑
  mobileNav.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';

  // 記錄原始位置（相對於文檔頂部的距離）
  let originalTop = null;
  
  const calculateOriginalTop = () => {
    // 確保計算時元素處於正常流
    const wasFixed = mobileNav.classList.contains('is-fixed');
    if (wasFixed) {
      mobileNav.classList.remove('is-fixed');
      Object.assign(mobileNav.style, {
        position: '',
        top: '',
        left: '',
        right: '',
        zIndex: ''
      });
      placeholder.style.height = '0px';
    }
    
    // 強制瀏覽器重排後再計算
    void mobileNav.offsetHeight;
    originalTop = mobileNav.getBoundingClientRect().top + window.scrollY;
    
    if (wasFixed) {
      // 恢復 fixed 狀態
      placeholder.style.height = mobileNav.offsetHeight + 'px';
      mobileNav.classList.add('is-fixed');
      Object.assign(mobileNav.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '900'
      });
    }
  };
  
  calculateOriginalTop();

  const setFixed = () => {
    if (mobileNav.classList.contains('is-fixed')) return;
    
    // 先設置 placeholder 高度，避免跳動
    const navHeight = mobileNav.offsetHeight;
    placeholder.style.height = navHeight + 'px';
    
    // 立即應用 fixed 狀態
    mobileNav.classList.add('is-fixed');
    Object.assign(mobileNav.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      zIndex: '900'
    });
  };

  const unsetFixed = () => {
    if (!mobileNav.classList.contains('is-fixed')) return;
    
    mobileNav.classList.remove('is-fixed');
    Object.assign(mobileNav.style, {
      position: '',
      top: '',
      left: '',
      right: '',
      zIndex: ''
    });
    placeholder.style.height = '0px';
  };

  // 防止在多個 second-nav 切換時產生瞬間跳動：延遲取消 fixed，並在延遲期間重新檢查狀態
  let unsetTimer = null;
  const scheduleUnsetFixed = (delay = 60) => {
    if (unsetTimer) clearTimeout(unsetTimer);
    unsetTimer = setTimeout(() => {
      // 重新計算是否仍然應該取消固定，若仍不該固定則真正取消
      const scrollY = window.scrollY || window.pageYOffset;
      const sectionRect = section.getBoundingClientRect();
      const sectionTopInViewport = sectionRect.top;
      const isSc2 = navId === 'sc2MobileSecondNav';
      // responsive trigger offset: 100 on mobile (<992), else 210
      const triggerOffset = window.innerWidth < 992 ? 100 : 210;
      
      let shouldBeFixedNow;
      if (window.innerWidth < 992) {
        // 手機版：sc2 只有碰到頂部才固定
        shouldBeFixedNow = isSc2 ? (scrollY >= originalTop) : (scrollY >= originalTop || sectionTopInViewport <= triggerOffset);
      } else {
        // 桌機版：全部都用統一條件
        shouldBeFixedNow = (scrollY >= originalTop || sectionTopInViewport <= triggerOffset);
      }
      
      if (!shouldBeFixedNow) {
        unsetFixed();
      }
      unsetTimer = null;
    }, delay);
  };

  const syncState = () => {
    // 僅在手機寬度啟用此行為
    // if (window.innerWidth >= 992) {
    //   unsetFixed();
    //   mobileNav.style.display = 'none'; // 桌面版隱藏
    //   return;
    // }
    
    // 取得當前滾動位置和區塊位置
    const scrollY = window.scrollY || window.pageYOffset;
    const viewportHeight = window.innerHeight;
    const sectionRect = section.getBoundingClientRect();
    const sectionTop = sectionRect.top + scrollY;
    const sectionBottom = sectionTop + sectionRect.height;
    
    // 判斷是否在對應區塊的顯示範圍內
    // 使用區塊相對於視窗的位置來判斷
    const sectionTopInViewport = sectionRect.top;
    const sectionBottomInViewport = sectionRect.bottom;
    
    // 提前觸發（因為 .contentTopWrapper 有 translateY）
  // 當區塊頂部到達視窗頂部某個位置時開始考慮顯示
    // 手機版 100，電腦版 210
    const triggerOffset = window.innerWidth < 992 ? 100 : 210;
    const bottomThreshold = triggerOffset;
    let isInSection = sectionTopInViewport <= triggerOffset && sectionBottomInViewport > bottomThreshold;

    // 只在 sc2-2, sc2-3, sc2-4 區塊顯示 sc2MobileSecondNav
    if (navId === 'sc2MobileSecondNav') {
      let shouldShow = false;
      
      // 手機版：需同時滿足在區塊範圍內且碰到頂部才顯示
      if (window.innerWidth < 992) {
        // 先檢查是否在 sc2-2, sc2-3, sc2-4 任一區塊範圍內
        let inTargetSection = false;
        [sc2_2, sc2_3, sc2_4].forEach(sec => {
          if (sec) {
            const rect = sec.getBoundingClientRect();
            const earlyHideOffset = sec.id === 'sc2-4' ? 300 : 0;
            if (rect.top <= triggerOffset && rect.bottom > earlyHideOffset) {
              inTargetSection = true;
            }
          }
        });
        // 手機版：需同時滿足「在目標區塊內」且「已滾動到 nav 原始位置」
        shouldShow = inTargetSection && (scrollY >= originalTop);
      } else {
        // 桌機版：保持原邏輯
        [sc2_2, sc2_3, sc2_4].forEach(sec => {
          if (sec) {
            const rect = sec.getBoundingClientRect();
            const earlyHideOffset = sec.id === 'sc2-4' ? 300 : 0;
            if (rect.top <= triggerOffset && rect.bottom > earlyHideOffset) {
              shouldShow = true;
            }
          }
        });
      }
      
      if (!shouldShow) {
        mobileNav.style.visibility = 'hidden';
        mobileNav.style.opacity = '0';
        mobileNav.style.pointerEvents = 'none';
        scheduleUnsetFixed();
        return;
      }
    }
    // sc5MobileSecondNav: 不在 sc5-1 顯示
    if (navId === 'sc5MobileSecondNav' && sc5_1) {
      const rect = sc5_1.getBoundingClientRect();
      // 如果 sc5-1 在視窗範圍內（頂部 <= triggerOffset 且底部 > 0），則隱藏 sc5MobileSecondNav
      if (rect.top <= triggerOffset && rect.bottom > 300) {
        mobileNav.style.visibility = 'hidden';
        mobileNav.style.opacity = '0';
        mobileNav.style.pointerEvents = 'none';
        scheduleUnsetFixed();
        return;
      }
    }

    const isSc2 = navId === 'sc2MobileSecondNav';
    let shouldBeFixed;
    if (window.innerWidth < 992) {
      // 手機版：sc2 用特殊條件（只有碰到頂部才固定），其餘用一般條件
      shouldBeFixed = isSc2 ? (scrollY >= originalTop) : (scrollY >= originalTop || sectionTopInViewport <= triggerOffset);
    } else {
      // 桌機版：全部都用 scrollY >= originalTop || sectionTopInViewport <= triggerOffset
      shouldBeFixed = (scrollY >= originalTop || sectionTopInViewport <= triggerOffset);
    }
    
    if (!isInSection) {
      // 不在對應區塊內，隱藏導航（使用 visibility 和 opacity 避免佈局抖動）
      mobileNav.style.visibility = 'hidden';
      mobileNav.style.opacity = '0';
      mobileNav.style.pointerEvents = 'none';
      // 隱藏時不立即 unsetFixed，避免往上滑時跳動
      // 只有當滾動位置回到原始位置以上時才 unsetFixed
      if (!shouldBeFixed) {
        scheduleUnsetFixed();
      }
      return;
    } else {
      // 在對應區塊內，顯示導航
      mobileNav.style.visibility = 'visible';
      mobileNav.style.opacity = '1';
      mobileNav.style.pointerEvents = '';
    }
    
    // 如果滾動到原始位置或以上，固定導航列
    if (shouldBeFixed) {
      setFixed();
    } else {
      // 如果滾回原始位置以上，取消固定
      unsetFixed();
    }
  };

  // 點擊 nav-item 切換 active 狀態，並處理錨點滾動
  const navItems = mobileNav.querySelectorAll('.nav-item');
  const sections = []; // 儲存所有目標區塊
  let isScrollingProgrammatically = false; // 標記是否正在程式化滾動
  
  navItems.forEach(item => {
    const link = item.querySelector('.nav-link');
    if (!link) return;
    
    const href = link.getAttribute('href');
    if (href && href.startsWith('#')) {
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        sections.push({
          id: targetId,
          element: targetElement,
          navItem: item
        });
      }
    }
    
    item.addEventListener('click', function(e) {
      // 移除所有 nav-item 的 active 類
      navItems.forEach(nav => nav.classList.remove('active'));
      // 給當前點擊的項目加上 active 類
      this.classList.add('active');
      
      // 處理錨點滾動，預留 50px 空間
  // 如果是 sc5 的第二導航，因為高度較高，預留更多空間
        // 分電腦版和手機版
        const isSc5Nav = navId === 'sc5MobileSecondNav';
        let clickOffset;
        if (window.innerWidth >= 992) {
          // 電腦版
          clickOffset = isSc5Nav ? 140 : 152;
        } else {
          // 手機版
          clickOffset = isSc5Nav ? 80 : 40;
        }

    if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const offsetTop = targetElement.getBoundingClientRect().top + window.scrollY;
      const scrollToPosition = offsetTop - clickOffset; // 預留空間
          
          // 設置標記，表示正在進行程式化滾動
          isScrollingProgrammatically = true;
          
          window.scrollTo({
            top: scrollToPosition,
            behavior: 'smooth'
          });
          
          // 滾動完成後重置標記（smooth scroll 大約需要 500-800ms）
          setTimeout(() => {
            isScrollingProgrammatically = false;
          }, 1000);
        }
      }
    });
  });

  // 滾動時自動切換 active 狀態
  const updateActiveOnScroll = () => {
    if (isScrollingProgrammatically) return; // 如果正在程式化滾動，跳過自動更新
    
    const scrollY = window.scrollY || window.pageYOffset;
    // 分電腦版和手機版
    let offset;
    if (window.innerWidth >= 992) {
      // 電腦版
      offset = 152;
    } else {
      // 手機版
      offset = 85;
    }
    
    let currentSection = null;
    
    // 找出當前滾動位置對應的區塊
    sections.forEach(section => {
      const rect = section.element.getBoundingClientRect();
      const sectionTop = rect.top + scrollY;
      
      if (scrollY + offset >= sectionTop) {
        currentSection = section;
      }
    });
    
    // 更新 active 狀態
    if (currentSection) {
      navItems.forEach(nav => nav.classList.remove('active'));
      currentSection.navItem.classList.add('active');
    }
  };

  // 將 updateActiveOnScroll 合併到滾動事件中
  let scrollTimer = null;
  window.addEventListener('scroll', () => {
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      syncState();
      updateActiveOnScroll();
    }, 10);
  }, { passive: true });

  // 初始與事件監聽
  syncState();
  updateActiveOnScroll(); // 初始化 active 狀態
  
  // 注意：scroll 事件已經在上面與 updateActiveOnScroll 合併處理
  let timer = null;
  window.addEventListener('resize', () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      // resize 時一律重新計算原始位置（無論寬度）
      const wasFixed = mobileNav.classList.contains('is-fixed');
      if (wasFixed) {
        // 暫時取消固定以獲得正確位置
        unsetFixed();
      }
      
      // 等待瀏覽器完成佈局重排
      setTimeout(() => {
        calculateOriginalTop();
        syncState();
        updateActiveOnScroll();
      }, 50);
    }, 80);
  });
}

// 初始化所有手機版第二導航
document.addEventListener('DOMContentLoaded', () => {
  initializeMobileSecondNav('sc2MobileSecondNav');
  // initializeMobileSecondNav('sc3MobileSecondNav');
  // initializeMobileSecondNav('sc4MobileSecondNav');
  initializeMobileSecondNav('sc5MobileSecondNav');
});

// PageFlip 書本翻頁效果
const book = document.getElementById('book');

const initializePageFlip = () => {
  const windowWidth = window.innerWidth;
  
  // 判斷裝置類型
  const isMobile = windowWidth < 768;      // 手機：< 768px
  const isTablet = windowWidth >= 768 && windowWidth < 992;  // 平板：768px ~ 991px
  const isDesktop = windowWidth >= 992;    // 桌面：≥ 992px
  
  // 根據裝置類型設定參數
  let config = {};
  
  if (isMobile) {
    // 手機設定
    config = {
      width: 350,
      height: 500,
      size: "stretch",
      minWidth: 290,
      maxWidth: 400,
      minHeight: 420,
      maxHeight: 600
    };
  } else if (isTablet) {
    // 平板設定
    config = {
      width: 450,
      height: 600,
      size: "stretch",
      minWidth: 400,
      maxWidth: 600,
      minHeight: 400,
      maxHeight: 600
    };
  } else {
    // 桌面設定
    config = {
      width: 550,
      height: 733,
      size: "stretch",
      minWidth: 500,
      maxWidth: 1000,
      minHeight: 600,
      maxHeight: 1350
    };
  }
  
  const pageFlip = new PageFlip(book, {
    width: config.width,
    height: config.height,
    size: config.size,
    minWidth: config.minWidth,
    maxWidth: config.maxWidth,
    minHeight: config.minHeight,
    maxHeight: config.maxHeight,
    maxShadowOpacity: 0.5, // 陰影不透明度
    showCover: false, // 不顯示封面
    mobileScrollSupport: false // 關閉手機捲動支援
  });

  pageFlip.loadFromHTML(document.querySelectorAll('.my-page'));
  setupPageControls(pageFlip);
  return pageFlip;
};


function setupPageControls(pageFlip) {
  const prevBtn = document.querySelector('.prevPage');
  const nextBtn = document.querySelector('.nextPage');

  const updatePageInfo = () => {
    const currentPage = pageFlip.getCurrentPageIndex() + 1;
    const totalPages = pageFlip.getPageCount();
    const windowWidth = window.innerWidth;
    
    // 判斷裝置類型（與 initializePageFlip 保持一致）
    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 992;
    const isDesktop = windowWidth >= 992;

    // 第一頁時隱藏「上一頁」按鈕
    prevBtn.style.visibility = currentPage === 1 ? 'hidden' : 'visible';

    // 根據模式正確判斷何時隱藏「下一頁」
    if (isMobile || isTablet) {
      // 單頁模式（手機 + 平板）→ 只有到最後一頁才隱藏
      nextBtn.style.visibility = currentPage === totalPages ? 'hidden' : 'visible';
    } else {
      // 雙頁模式（桌面）→ 倒數第二個 spread（例如第 9/10 頁）就隱藏
      nextBtn.style.visibility = currentPage >= totalPages - 1 ? 'hidden' : 'visible';
    }
  };

  updatePageInfo();

  prevBtn.addEventListener('click', () => pageFlip.flipPrev());
  nextBtn.addEventListener('click', () => pageFlip.flipNext());

  pageFlip.on('flip', updatePageInfo);
}

let pageFlipInstance = initializePageFlip();



  // Elements to inject
  var mySVGsToInject = document.querySelectorAll('img.svgImg');

  // Do the injection
  SVGInjector(mySVGsToInject);

  // // 點擊遮罩隱藏並啟動 YouTube 影片（支援多個 .videoblock）
  // document.querySelectorAll('.videoblock').forEach(function(block){
  //   var mask = block.querySelector('.mask');
  //   var iframe = block.querySelector('iframe');
  //   if(!mask || !iframe) return;

  //   // 讓遮罩可聚焦並顯示為可點擊
  //   mask.setAttribute('tabindex', '0');
  //   mask.style.cursor = 'pointer';

  //   function playVideo(){
  //     // 隱藏遮罩
  //     mask.style.display = 'none';
  //     // 在 src 後加入 autoplay=1（若尚未加入）
  //     var src = iframe.getAttribute('src') || '';
  //     if(src.indexOf('autoplay=1') === -1){
  //       var sep = src.indexOf('?') === -1 ? '?' : '&';
  //       iframe.setAttribute('src', src + sep + 'autoplay=1');
  //     }
  //     // 對 iframe 聚焦（輔助無障礙）
  //     try { iframe.focus(); } catch(e){}
  //   }

  //   mask.addEventListener('click', playVideo);
  //   mask.addEventListener('keydown', function(e){
  //     if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); playVideo(); }
  //   });
  // });

// Swiper 初始化
  const swiperSc2_1 = new Swiper('#swiperSc2-1', {
    loop: true,
    navigation: {
      nextEl: '.nextSc2-1',
      prevEl: '.prevSc2-1',
    },
  });

  let $lgSwiper = document.getElementById('swiperLightGallery');
    const swiperSc2_2 = new Swiper('#swiperSc2-2', {
    loop: true,
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 16
      },
      992: {
        slidesPerView: 3,
        spaceBetween: 24
      }
    },
    navigation: {
      nextEl: '.nextSc2-2',
      prevEl: '.prevSc2-2',
    },
    on: {
        init: function () {
            const lg = lightGallery($lgSwiper, {
                plugins: [lgZoom],
                speed: 500,
                download: false,
                mobileSettings: {
                    controls: true,
                    showCloseIcon: true,
                    download: false
                }
            });
            $lgSwiper.addEventListener('lgBeforeClose', () => {
                swiperSc2_2.slideTo(lg.index, 0)
            });
        },
    }
  });

  
  const swiperSc2_3 = new Swiper('#swiperSc2-3', {
    loop: true,
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 8
      },
      992: {
        slidesPerView: 2,
        spaceBetween: 8,
        slidesPerGroup: 2 // 一次滾動兩張 
      }
    },
    navigation: {
      nextEl: '.nextSc2-3',
      prevEl: '.prevSc2-3',
    },
  });
  const swiperSc2_3_2 = new Swiper('#swiperSc2-3-2', {
    loop: true,
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 8
      },
      992: {
        slidesPerView: 2,
        spaceBetween: 8,
        slidesPerGroup: 2 // 一次滾動兩張 
      }
    },
    navigation: {
      nextEl: '.nextSc2-3-2',
      prevEl: '.prevSc2-3-2',
    },
  });

// 點點背景生成
  (function(){
    const container = document.getElementById('dotGrid');
    if(!container) return;
    const frag = document.createDocumentFragment();
    const src = './images/sc2/sc2-4-note.webp';
    const total = 84;
    for(let i=0;i<total;i++){
      const img = document.createElement('img');
      img.src = src;
      img.alt = 'dot';
      img.className = 'dotItem';
      frag.appendChild(img);
    }
    container.appendChild(frag);
  })();

// // 點擊導航項目時滑動到對應區塊並切換 tab
// window.switchTabAndScroll = function(element) {
//   const targetId = element.getAttribute('data-target');
//   console.log('targetId:', targetId); // 調試用
  
//   if (!targetId) {
//     console.log('No targetId found');
//     return;
//   }
  
//   // 移除 # 號
//   const tabId = targetId.replace('#', '');
//   console.log('Looking for tab button:', `#${tabId}-tab`); // 調試用
  
//   // 找到對應的 tab 按鈕
//   const tabButton = document.querySelector(`#${tabId}-tab`);
//   console.log('Found tab button:', tabButton); // 調試用
  
//   if (tabButton) {
//     // 直接點擊按鈕觸發 tab 切換
//     tabButton.click();
//   } else {
//     console.log('Tab button not found:', `#${tabId}-tab`);
//   }
  
//   // 找到 tab 所在的區塊
//   const tabPane = document.querySelector(targetId);
//   console.log('Found tab pane:', tabPane); // 調試用
  
//   if (!tabPane) {
//     console.log('Tab pane not found:', targetId);
//     return;
//   }
  
//   // 找到包含 tab 的主要區塊（通常是最近的 section）
//   const mainSection = tabPane.closest('section') || tabPane.closest('[id^="sc"]');
//   console.log('Found main section:', mainSection); // 調試用
  
//   if (!mainSection) {
//     console.log('Main section not found');
//     return;
//   }
  
//   // 等待 tab 切換動畫完成後再滾動
//   setTimeout(() => {
//     const offsetTop = mainSection.getBoundingClientRect().top + window.scrollY;
//     const navHeight = 350; // 預留 350px 空間
//     const scrollToPosition = offsetTop - navHeight;
    
//     console.log('Scrolling to:', scrollToPosition); // 調試用
    
//     window.scrollTo({
//       top: scrollToPosition,
//       behavior: 'smooth'
//     });
//   }, 100); // 延遲 100ms 確保 tab 切換完成
// };

// 同步兩個 sc3Nav sc4Nav 的 tab 切換
document.addEventListener('DOMContentLoaded', () => {
  function setupNavPin(navEl, endTrigger) {
    if (!navEl) return

    const originalBorderRadius = window.getComputedStyle(navEl).borderRadius // ⬅️ 記住原本的圓角

    // 手機版樣式應用函數
    const applyMobileStyles = () => {
      navEl.style.width = '100vw'
      navEl.style.maxWidth = '100vw'
      navEl.style.left = '0'
      navEl.style.right = '0'
      navEl.style.position = 'fixed'
      navEl.style.borderRadius = '0' // 取消圓角
    }

    // 移除手機版樣式函數
    const removeMobileStyles = () => {
      navEl.style.width = ''
      navEl.style.maxWidth = ''
      navEl.style.left = ''
      navEl.style.right = ''
      navEl.style.position = ''
      navEl.style.borderRadius = originalBorderRadius
    }

    gsap.to(navEl, {
      ease: 'none',
      scrollTrigger: {
        trigger: navEl,
        start: window.innerWidth < 992 ? 'top top' : (window.innerWidth <= 1200 ? 'top 63px' : 'top 98px'),
        endTrigger: endTrigger,
        end: window.innerWidth < 992 ? 'bottom 100px' : (window.innerWidth <= 1200 ? 'bottom 250px' : 'bottom 200px'),
        pin: true,
        pinSpacing: false,
        onEnter: () => {
          // 只在手機版（< 992px）設置 100vw
          if (window.innerWidth < 992) {
            applyMobileStyles()
          }
        },
        onLeave: () => {
          if (window.innerWidth < 992) {
            removeMobileStyles()
          }
        },
        onEnterBack: () => {
          if (window.innerWidth < 992) {
            applyMobileStyles()
          }
        },
        onLeaveBack: () => {
          if (window.innerWidth < 992) {
            removeMobileStyles()
          }
        },
        onRefresh: (self) => {
          // 當 ScrollTrigger refresh 時，如果在 pin 狀態且是手機版，確保樣式正確
          if (window.innerWidth < 992 && self.isActive) {
            applyMobileStyles()
          }
        },
      },
    })
  }

  const sc3Nav = document.getElementById("sc3Nav");
  const sc4Nav = document.getElementById("sc4Nav");

  setupNavPin(sc3Nav, "#sc3-2");
  setupNavPin(sc4Nav, "#sc4-2");

});

// 點擊任意 tab 後滾動該 tab pane，使其頂部距離視窗頂部 200px
document.addEventListener('DOMContentLoaded', () => {
  // 使用 Bootstrap 事件 'shown.bs.tab'，確保 tab 已被顯示
  document.addEventListener('shown.bs.tab', (e) => {
    try {
      const activated = e.target; // 被啟用的 tab button
      if (!activated) return;

      // 優先使用 data-bs-target，其次使用 href
      const selector = activated.getAttribute('data-bs-target') || activated.getAttribute('href');
      if (!selector) return;

      const pane = document.querySelector(selector);
      if (!pane) return;

      // 先執行 ScrollTrigger refresh，然後再計算滾動位置
      const performScroll = () => {
        // 重新取得位置（因為 refresh 後可能會改變）
        const offsetTop = pane.getBoundingClientRect().top + window.scrollY;
        
        // 分手機/電腦版預留距離，sc3Nav=120，sc4Nav=90（手機），其他tab=80
        let offset = 200;
        if (window.innerWidth < 992) {
          // 判斷目前啟用的 tab 屬於 sc3Nav 或 sc4Nav
          const parentNav = activated.closest('#sc3Nav') ? 'sc3Nav' : (activated.closest('#sc4Nav') ? 'sc4Nav' : null);
          if (parentNav === 'sc3Nav') {
            offset = 125;
          } else if (parentNav === 'sc4Nav') {
            offset = 125;
          } else {
            offset = 80;
          }
        }
        const scrollToPosition = offsetTop - offset;

        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
      };

      // 先 refresh ScrollTrigger，再執行滾動
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
        // 等待 refresh 完成後再滾動
        setTimeout(performScroll, 100);
      } else {
        // 如果沒有 ScrollTrigger，直接滾動
        performScroll();
      }
    } catch (err) {
      // silent fail
    }
  }, false);
});

// Side nav mobile toggle: 點擊按鈕展開/收合側邊選單，並切換按鈕文字與 icon
document.addEventListener('DOMContentLoaded', () => {
  const sideNav = document.getElementById('sideNav');
  if (!sideNav) return;

  const btn = document.getElementById('sideNavBtn');
  const list = sideNav.querySelector('.sideNav_list');
  if (!btn || !list) return;

  const openText = '收合';
  const closeText = '展開';

  const setOpen = (open) => {
    if (open) {
      sideNav.classList.add('open');
      btn.querySelector('span').textContent = openText;
      // icon rotate handled via CSS
    } else {
      sideNav.classList.remove('open');
      btn.querySelector('span').textContent = closeText;
    }
  };

  // initial state: closed on mobile
  setOpen(false);

  btn.addEventListener('click', (e) => {
    const isOpen = sideNav.classList.contains('open');
    setOpen(!isOpen);
  });

  // close when clicking a link inside
  sideNav.querySelectorAll('a').forEach(a => {
    a.addEventListener('click', () => setOpen(false));
  });

  // close when clicking outside the list (only on mobile)
  document.addEventListener('click', (e) => {
    if (window.innerWidth >= 992) return; // only mobile
    if (!sideNav.classList.contains('open')) return;
    const target = e.target;
    if (!sideNav.contains(target) && target !== btn) {
      setOpen(false);
    }
  });

  // escape key closes
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sideNav.classList.contains('open')) {
      setOpen(false);
    }
  });
});

